// Code-action: "Qualify ambiguous column" — Slice 11.
//
// Triggered by the "column is ambiguous" server diagnostic (MSSQL 209,
// PG 42702, MySQL ER_NON_UNIQ_ERROR / 1052, Oracle ORA-00918) and by a
// resolver-side rule for SQLite (where the engine emits "ambiguous column
// name" without a numeric code): when the cursor sits on a bare column
// reference that exists in 2+ scope-reachable tables, surface one quickfix
// per candidate that replaces the bare identifier with `prefix.column`.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 11.
//
// Strategy:
//   1. Find the bare identifier at the cursor (single-part dotted chain).
//   2. Parse the buffer + locate the cursor's enclosing statement.
//   3. Run `extractQueryStructure` to enumerate FROM items + JOINs in scope.
//   4. For each FROM-target relation, walk the OE tree (Phase 1's stand-in
//      for the SchemaSnapshot, established by Slice 5/6) to confirm that
//      the relation owns the column. Aliased FROM items prefer the alias
//      text; un-aliased relations use the bare table name.
//   5. If 2+ relations own the column, emit one CodeActionEdit per candidate.
//
// Quoting style is preserved by leaving the original column-name bytes in
// place and prefixing `prefix.` ahead of them — `emitSource`'s right-to-left
// patch invariant (NAV5) holds because we only INSERT, never overwrite the
// source identifier bytes.

import type { ParseOptions, SqlEngine } from '../../../sql-grammar/model';
import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '../../../sql-grammar/document';
import { findDottedChainStart, parseDottedChain } from '../../../sql-resolver/dotted/dottedParts';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
import type { ParsedBuffer } from '../../../sql-resolver/types';
import type { DatabaseExplorerNode } from '../../../explorer/models/DatabaseTreeModels';
import { useDatabaseStore } from '../../../database-objects/store/databaseStore';
import { databaseEngineIdToSqlEngine } from '../../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { extendCursorToIdentEnd } from '../../go-to/cursorIdent';
import type {
  CodeActionContext,
  CodeActionEdit,
  CodeActionRegistryEntry,
  SqlRange,
} from '../../types';

/** Server-emitted diagnostic codes for "ambiguous column". MSSQL "209",
 *  PG SQLSTATE "42702", MySQL "ER_NON_UNIQ_ERROR" / "1052", Oracle "ORA-00918"
 *  / "918". SQLite emits no specific code — detection there falls back to the
 *  resolver rule (cursor on a bare ident matched by 2+ FROM relations). The
 *  set is exported so future ambiguous-column-aware fixes can reuse the
 *  canonical mapping. */
export const AMBIGUOUS_COLUMN_DIAGNOSTIC_CODES: ReadonlySet<string> = new Set<string>([
  '209',
  '42702',
  'ER_NON_UNIQ_ERROR',
  '1052',
  'ORA-00918',
  '918',
]);

const ENTRY_ID = 'qualify-ambiguous-column' as const;

export const qualifyAmbiguousColumnEntry: CodeActionRegistryEntry = {
  id: ENTRY_ID,
  kind: 'quickfix',
  title: 'Qualify ambiguous column',
  canApply: (ctx) => collectFixes(ctx, readLiveTree).length > 0,
  buildEdits: (ctx) => collectFixes(ctx, readLiveTree),
};

/** Test seam — lets unit tests pass a hand-built OE tree without touching
 *  the live Zustand store. Mirrors `FindColumnDeps.getTree` from Slice 6's
 *  `columnAtCursor.ts`. The production registry entry above uses
 *  `readLiveTree`. */
export function collectQualifyAmbiguousColumnEditsForTest(
  ctx: CodeActionContext,
  getTree: () => readonly DatabaseExplorerNode[],
): readonly CodeActionEdit[] {
  return collectFixes(ctx, getTree);
}

type Candidate = {
  /** Display prefix for the qualified column (alias > table name). */
  readonly prefix: string;
  /** Stable ordering key so multi-engine output is deterministic. */
  readonly orderKey: string;
};

function collectFixes(
  ctx: CodeActionContext,
  getTree: () => readonly DatabaseExplorerNode[],
): readonly CodeActionEdit[] {
  const source = ctx.sourceText;
  if (!source || source.trim().length === 0) return [];
  const connectionId = ctx.connectionId;
  if (!connectionId) return [];

  const sqlEngine = databaseEngineIdToSqlEngine(ctx.engineId) ?? 'mssql';
  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };

  // 1. Locate the bare identifier under the cursor.
  const cursorAtEnd = extendCursorToIdentEnd(source, ctx.cursor.offset);
  const chainStart = findDottedChainStart(source, cursorAtEnd, options);
  const chain = parseDottedChain(source, chainStart, cursorAtEnd, options);
  if (!chain || chain.parts.length !== 1) return [];

  const colPart = chain.parts[0];
  const identRange = colPart.range;
  const columnFolded = colPart.folded.toLowerCase();

  // 2. Parse buffer + find cursor-enclosing statement + extract structure.
  const buffer = parseBuffer(source, options);
  if (!buffer) return [];
  const stmt = buffer.documentStructure.statementAt(ctx.cursor.offset)
    ?? buffer.documentStructure.statements[0]
    ?? null;
  if (!stmt) return [];

  let structure;
  try {
    structure = extractQueryStructure(buffer, stmt);
  } catch {
    return [];
  }
  if (structure.kind !== 'select') return [];

  // 3. Enumerate scope-reachable relations: FROM items + JOIN right-hand items.
  const relations = collectScopeRelations(structure);
  if (relations.length < 2) return [];

  // 4. Filter to relations that own a column with the bare identifier's name.
  const treeData = getTree();
  const owners = relations.filter((r) =>
    relationOwnsColumn(treeData, connectionId, ctx.database ?? null, r.qualified, columnFolded),
  );
  if (owners.length < 2) return [];

  // 5. Build candidate prefixes — alias when present, else the unqualified
  //    table name. Stable-sort by orderKey so the action menu's order is
  //    deterministic across engines / runs.
  const candidates: Candidate[] = owners.map((r) => ({
    prefix: r.alias ?? r.qualified.name,
    orderKey: (r.alias ?? r.qualified.name).toLowerCase(),
  }));
  candidates.sort((a, b) => (a.orderKey < b.orderKey ? -1 : a.orderKey > b.orderKey ? 1 : 0));

  return candidates.map<CodeActionEdit>((c) => ({
    id: ENTRY_ID,
    description: `Qualify as ${c.prefix}.${colPart.value}`,
    textEdits: [
      {
        range: prefixInsertionRange(identRange),
        newText: `${c.prefix}.`,
      },
    ],
  }));
}

// ─── Helpers ───────────────────────────────────────────────────────────────

type ScopeRelation = {
  readonly qualified: { readonly name: string; readonly schema?: string; readonly database?: string };
  readonly alias?: string;
};

function collectScopeRelations(structure: ReturnType<typeof extractQueryStructure>): readonly ScopeRelation[] {
  const out: ScopeRelation[] = [];
  for (const fi of structure.fromItems ?? []) {
    pushRelation(out, fi);
  }
  for (const j of structure.joins ?? []) {
    pushRelation(out, j.right);
  }
  return out;
}

function pushRelation(out: ScopeRelation[], fi: { target: { kind: string; qualified?: { name: string; schema?: string; database?: string } }; alias?: { alias: string } }): void {
  if (fi.target.kind !== 'relation' || !fi.target.qualified) return;
  out.push({
    qualified: {
      name: fi.target.qualified.name,
      schema: fi.target.qualified.schema,
      database: fi.target.qualified.database,
    },
    alias: fi.alias?.alias,
  });
}

function parseBuffer(source: string, options: ParseOptions): ParsedBuffer | null {
  try {
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'qualify-ambiguous-column');
    return { parsedDocument, documentStructure };
  } catch {
    return null;
  }
}

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

// Insertion-only edit at the START of the bare identifier — preserves the
// original quoting bytes (so `"Email"` stays as `"Email"` after the fix).
function prefixInsertionRange(identRange: SqlRange): SqlRange {
  return {
    startOffset: identRange.startOffset,
    endOffset: identRange.startOffset,
    startLine: identRange.startLine,
    startColumn: identRange.startColumn,
    endLine: identRange.startLine,
    endColumn: identRange.startColumn,
  };
}

function readLiveTree(): readonly DatabaseExplorerNode[] {
  return useDatabaseStore.getState().treeData;
}

// eslint-disable-next-line complexity
function relationOwnsColumn(
  treeData: readonly DatabaseExplorerNode[],
  connectionId: string,
  database: string | null,
  relation: { name: string; schema?: string; database?: string },
  columnFolded: string,
): boolean {
  const targetName = relation.name.toLowerCase();
  // Permissive schema matching mirrors `lookupRelationWithColumn` in
  // columnAtCursor.ts (Slice 6 fix): when the user-typed identifier has no
  // schema part, accept any OE-tree schema; only reject when both sides are
  // non-empty and disagree. Some engines project schemas that
  // `defaultSchemaForEngine` doesn't predict — mysql's current-database is
  // `''`, sqlite uses `'main'`, oracle uses the upper-cased login — so a
  // strict default-vs-tree compare blocks the unqualified-cursor fix on
  // those engines (NAV6 real-DB regression caught by Slice 11's per-engine
  // E2E).
  const userTypedSchema = relation.schema?.toLowerCase();
  const requestedDb = (relation.database ?? database ?? '').toLowerCase();

  let found = false;
  walk(treeData, undefined);
  return found;

  function walk(nodes: readonly DatabaseExplorerNode[] | undefined, currentDb: string | undefined): void {
    if (!nodes || found) return;
    for (const node of nodes) {
      if (found) return;
      if (node.type === 'connection') {
        const meta = node.metadata as { id?: string } | undefined;
        if (meta?.id !== connectionId) continue;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, undefined);
        continue;
      }
      if (node.type === 'database') {
        const meta = node.metadata as { name?: string } | undefined;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, meta?.name);
        continue;
      }
      if (node.type === 'table' || node.type === 'view') {
        const meta = node.metadata as { objectDetails?: { name?: string; schema?: string } } | undefined;
        const od = meta?.objectDetails;
        if (od?.name && od.name.toLowerCase() === targetName) {
          if (requestedDb && (currentDb ?? '').toLowerCase() !== requestedDb) {
            walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
            continue;
          }
          if (userTypedSchema && (od.schema ?? '').toLowerCase() !== userTypedSchema) {
            walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
            continue;
          }
          if (collectColumnNames(node.children as readonly DatabaseExplorerNode[] | undefined).some((cn) => cn.toLowerCase() === columnFolded)) {
            found = true;
            return;
          }
        }
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
        continue;
      }
      walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
    }
  }
}

function collectColumnNames(children: readonly DatabaseExplorerNode[] | undefined): readonly string[] {
  if (!children) return [];
  const out: string[] = [];
  for (const child of children) {
    if (child.type === 'columns-folder') {
      const grand = child.children as readonly DatabaseExplorerNode[] | undefined;
      for (const col of grand ?? []) {
        if (col.type === 'column') {
          const meta = col.metadata as { columnName?: string } | undefined;
          if (meta?.columnName) out.push(meta.columnName);
        }
      }
      continue;
    }
    if (child.type === 'column') {
      const meta = child.metadata as { columnName?: string } | undefined;
      if (meta?.columnName) out.push(meta.columnName);
    }
  }
  return out;
}
